import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ShieldCheck, Loader2, LogOut, Plus, X, RefreshCw, Power, PowerOff,
  Trash2, Clock, Users, Building2, TrendingUp, Activity, AlertTriangle,
  DollarSign, Wallet, CreditCard, Eye, FileDown, CalendarRange
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import { formatApiError } from '@/lib/utils';
import { showAlert } from '@/components/ui/GlobalAlert';
import { showConfirm } from '@/components/ui/GlobalConfirm';
import { showPrompt } from '@/components/ui/GlobalPrompt';

const BASE = process.env.REACT_APP_BACKEND_URL || '';
const SECRET_KEY = 'lexorbit_admin_secret';

const IC = "w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#888]/50 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C]";

const PLAN_LABELS = {
  independiente: 'Independiente',
  estudio_3: 'Estudio hasta 3',
  estudio_5: 'Estudio hasta 5',
  estudio_10: 'Estudio hasta 10',
  custom: 'A medida',
};

const ESTADO_COLOR = {
  activo: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/25',
  trial: 'text-[#C9A44C] bg-[#C9A44C]/10 border-[#C9A44C]/25',
  suspendido: 'text-red-400 bg-red-500/10 border-red-500/25',
  cancelado: 'text-[#888] bg-white/5 border-white/10',
};

const fmt = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const fmtF = (s) => { try { return new Date(s).toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return s || '—'; } };

function client(secret) {
  return axios.create({ baseURL: BASE, headers: { 'X-Admin-Secret': secret } });
}

/* ─── Modal: crear org de prueba ──────────────────────────── */
function ModalCrearOrg({ api, onClose, onCreated }) {
  const [f, setF] = useState({ nombre_org: '', plan: 'independiente', nombre_admin: '', email_admin: '', password: '', dias_acceso: 30 });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setSaving(true);
    try {
      await api.post('/api/superadmin/organizaciones/crear-prueba', { ...f, dias_acceso: Number(f.dias_acceso) });
      onCreated();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail, 'No se pudo crear la organización'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>Nueva organización de prueba</h3>
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
        </div>
        {err && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 mb-4 text-red-400 text-xs">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <input className={IC} placeholder="Nombre del estudio / organización *" value={f.nombre_org} onChange={set('nombre_org')} required minLength={2} />
          <select className={IC} value={f.plan} onChange={set('plan')}>
            {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k} className="bg-[#141414]">{v}</option>)}
          </select>
          <input className={IC} placeholder="Nombre del administrador *" value={f.nombre_admin} onChange={set('nombre_admin')} required />
          <input className={IC} type="email" placeholder="Email del administrador *" value={f.email_admin} onChange={set('email_admin')} required />
          <input className={IC} type="password" placeholder="Contraseña inicial *" value={f.password} onChange={set('password')} required minLength={6} />
          <div>
            <label className="block text-[10px] text-[#666] mb-1">Días de acceso</label>
            <input className={IC} type="number" min={1} max={365} value={f.dias_acceso} onChange={set('dias_acceso')} required />
          </div>
          <button type="submit" disabled={saving}
            className="w-full text-[#070707] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear organización'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Tarjeta de stat ──────────────────────────────────────── */
function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4">
      <div className="flex items-center gap-2 text-[#888] text-xs mb-2">
        <Icon className="w-4 h-4 text-[#C9A44C]" /> {label}
      </div>
      <div className="text-2xl font-semibold text-[#F8F6F0]">{value}</div>
      {sub && <div className="text-[11px] text-[#666] mt-1">{sub}</div>}
    </div>
  );
}

/* ─── Pantalla de acceso ───────────────────────────────────── */
function Gate({ onAuth }) {
  const [secret, setSecret] = useState('');
  const [err, setErr] = useState('');
  const [checking, setChecking] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setChecking(true);
    try {
      await client(secret).get('/api/superadmin/stats');
      sessionStorage.setItem(SECRET_KEY, secret);
      onAuth(secret);
    } catch {
      setErr('Clave incorrecta');
    } finally { setChecking(false); }
  };

  return (
    <div className="min-h-screen bg-[#070707] flex items-center justify-center px-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
      <form onSubmit={submit} className="w-full max-w-sm bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5 text-[#C9A44C]">
          <ShieldCheck className="w-5 h-5" />
          <h1 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>Panel SuperAdmin</h1>
        </div>
        {err && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 mb-4 text-red-400 text-xs">{err}</div>}
        <input className={IC + ' mb-4'} type="password" placeholder="Clave de administrador" value={secret} onChange={(e) => setSecret(e.target.value)} autoFocus required />
        <button type="submit" disabled={checking}
          className="w-full text-[#070707] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
          {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ingresar'}
        </button>
      </form>
    </div>
  );
}

/* ─── Panel principal ──────────────────────────────────────── */
function Panel({ secret, onLogout }) {
  const api = client(secret);
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState(null);
  const [actividad, setActividad] = useState(null);
  const [ingresos, setIngresos] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCrear, setShowCrear] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [purgando, setPurgando] = useState(false);
  const [exportando, setExportando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o, a, i, r] = await Promise.all([
        api.get('/api/superadmin/stats'),
        api.get('/api/superadmin/organizaciones'),
        api.get('/api/superadmin/actividad?limite=20'),
        api.get('/api/superadmin/ingresos'),
        api.get('/api/superadmin/resumen-mensual?meses=12'),
      ]);
      setStats(s.data); setOrgs(o.data); setActividad(a.data); setIngresos(i.data); setResumen(r.data);
    } catch (e) {
      if (e.response?.status === 403) onLogout();
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secret]);

  const exportarPdf = async () => {
    setExportando(true);
    try {
      const r = await api.get('/api/superadmin/exportar-pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `lexorbit_reporte_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      showAlert(formatApiError(e.response?.data?.detail, 'No se pudo generar el PDF'));
    } finally { setExportando(false); }
  };

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const id = setInterval(cargar, 30000);
    return () => clearInterval(id);
  }, [cargar]);

  const accion = async (org, tipo) => {
    setBusyId(org.id);
    try {
      if (tipo === 'activar') await api.post(`/api/superadmin/organizaciones/${org.id}/activar`);
      else if (tipo === 'suspender') await api.post(`/api/superadmin/organizaciones/${org.id}/suspender`);
      else if (tipo === 'extender') {
        const dias = await showPrompt('¿Cuántos días querés extender el período de prueba?', {
          titulo: 'Extender prueba',
          valorInicial: '7',
          tipo: 'number',
          placeholder: 'Días',
          confirmText: 'Extender',
        });
        if (dias === null || !(Number(dias) > 0)) return;
        await api.post(`/api/superadmin/organizaciones/${org.id}/extender-trial?dias=${Number(dias)}`);
      } else if (tipo === 'eliminar') {
        if (!(await showConfirm(`Esto elimina "${org.nombre}" y TODOS sus datos de forma permanente. ¿Confirmás?`, { titulo: 'Eliminar organización' }))) return;
        await api.delete(`/api/superadmin/organizaciones/${org.id}`);
      }
      await cargar();
    } catch (e) {
      showAlert(formatApiError(e.response?.data?.detail, 'Ocurrió un error'));
    } finally { setBusyId(null); }
  };

  const purgarHuerfanos = async () => {
    if (!(await showConfirm(
      'Borra de forma permanente todo dato (clientes, expedientes, etc.) cuyo org_id ya no exista, por orgs eliminadas con la versión vieja. Es seguro y no toca orgs activas. ¿Continuar?',
      { titulo: 'Purgar datos huérfanos' }))) return;
    setPurgando(true);
    try {
      const r = await api.post('/api/superadmin/purgar-huerfanos');
      const n = r.data?.filas_eliminadas ?? 0;
      showAlert(n ? `Limpieza completa: ${n} filas huérfanas eliminadas.` : 'No había datos huérfanos. Todo limpio.', 'success');
      await cargar();
    } catch (e) {
      showAlert(formatApiError(e.response?.data?.detail, 'No se pudo purgar'));
    } finally { setPurgando(false); }
  };

  return (
    <div className="min-h-screen bg-[#070707] text-[#F8F6F0]" style={{ fontFamily: 'Outfit, sans-serif' }}>
      <header className="h-[72px] sticky top-0 z-20 bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-[rgba(201,164,76,0.12)] flex items-center justify-between px-5">
        <div className="flex items-center gap-2 text-[#C9A44C]">
          <ShieldCheck className="w-5 h-5" />
          <h1 className="text-lg tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>Panel SuperAdmin</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportarPdf} disabled={exportando}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#0E0E0E] rounded-lg px-3 py-1.5 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }} title="Descargar reporte completo en PDF">
            {exportando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
            {exportando ? 'Generando…' : 'Exportar PDF'}
          </button>
          <button onClick={cargar} className="p-2 text-[#888] hover:text-[#F8F6F0] transition-colors" title="Actualizar">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={onLogout} className="flex items-center gap-1.5 text-xs text-[#888] hover:text-red-400 transition-colors px-2 py-1.5">
            <LogOut className="w-4 h-4" /> Salir
          </button>
        </div>
      </header>

      <main className="p-5 sm:p-7 max-w-[1400px] mx-auto space-y-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard icon={Building2} label="Organizaciones" value={stats.orgs.total} />
            <StatCard icon={Power} label="Activas" value={stats.orgs.activas} />
            <StatCard icon={Clock} label="En prueba" value={stats.orgs.en_trial} />
            <StatCard icon={PowerOff} label="Suspendidas" value={stats.orgs.suspendidas} />
            <StatCard icon={Users} label="Usuarios" value={stats.usuarios.total} sub={`+${stats.usuarios.nuevos_este_mes} este mes`} />
            <StatCard icon={TrendingUp} label="Nuevas este mes" value={stats.registros.este_mes} sub={`${stats.registros.crecimiento_pct >= 0 ? '+' : ''}${stats.registros.crecimiento_pct}% vs mes anterior`} />
          </div>
        )}

        {/* Resumen mensual */}
        {resumen?.meses?.length > 0 && (
          <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
              <CalendarRange className="w-4 h-4 text-[#C9A44C]" />
              <h2 className="text-sm font-medium">Resumen mensual · comparación mes a mes</h2>
            </div>

            <div className="p-4 border-b border-white/5">
              <div className="text-xs font-semibold text-[#C9A44C] uppercase tracking-wide mb-4">Organizaciones nuevas por mes</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={resumen.meses}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="label" stroke="#666" fontSize={10} tickFormatter={(l) => (l || '').split(' ')[0]} />
                  <YAxis stroke="#666" fontSize={11} allowDecimals={false} />
                  <Tooltip cursor={{ fill: 'rgba(201,164,76,0.06)' }}
                    contentStyle={{ background: '#0E0E0E', border: '1px solid rgba(201,164,76,0.2)', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: '#F8F6F0' }} formatter={(v) => [v, 'Orgs nuevas']} />
                  <Bar dataKey="orgs_nuevas" name="Orgs nuevas" fill="#C9A44C" radius={[4, 4, 0, 0]} maxBarSize={46} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[#666] uppercase tracking-wide text-[10px] border-b border-white/5">
                    <th className="px-4 py-2.5">Mes</th>
                    <th className="px-4 py-2.5 text-center">Orgs nuevas</th>
                    <th className="px-4 py-2.5 text-center">Δ vs mes ant.</th>
                    <th className="px-4 py-2.5 text-center">Usuarios nuevos</th>
                    <th className="px-4 py-2.5 text-center">Suscripciones</th>
                    <th className="px-4 py-2.5 text-right">Ingreso estimado</th>
                  </tr>
                </thead>
                <tbody>
                  {[...resumen.meses].reverse().map((m) => {
                    const d = m.delta_orgs_pct;
                    return (
                      <tr key={m.mes} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                        <td className="px-4 py-2.5 font-medium">{m.label}</td>
                        <td className="px-4 py-2.5 text-center">{m.orgs_nuevas}</td>
                        <td className="px-4 py-2.5 text-center">
                          {d === null || d === undefined ? (
                            <span className="text-[#555]">—</span>
                          ) : (
                            <span className={d >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                              {d >= 0 ? '▲' : '▼'} {Math.abs(d)}%
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-center text-[#A0A0A0]">{m.usuarios_nuevos}</td>
                        <td className="px-4 py-2.5 text-center text-[#A0A0A0]">{m.subs_activadas}</td>
                        <td className="px-4 py-2.5 text-right text-[#C9A44C] font-semibold">{fmt(m.ingreso)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Visitas a la página */}
        {stats?.visitas && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={Eye} label="Visitas totales" value={(stats.visitas.total || 0).toLocaleString('es-AR')} />
              <StatCard icon={Eye} label="Visitas hoy" value={(stats.visitas.hoy || 0).toLocaleString('es-AR')} />
              <StatCard icon={Eye} label="Esta semana" value={(stats.visitas.esta_semana || 0).toLocaleString('es-AR')} />
              <StatCard icon={Eye} label="Este mes" value={(stats.visitas.este_mes || 0).toLocaleString('es-AR')} />
            </div>
            <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4">
              <div className="text-xs font-semibold text-[#C9A44C] uppercase tracking-wide mb-4">Visitas (últimos 30 días)</div>
              {stats.visitas.diarias?.length ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={stats.visitas.diarias}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis dataKey="dia" stroke="#666" fontSize={10} minTickGap={24} tickFormatter={(d) => (d || '').slice(5)} />
                    <YAxis stroke="#666" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: '#0E0E0E', border: '1px solid rgba(201,164,76,0.2)', borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: '#F8F6F0' }} formatter={(v) => [v, 'Visitas']} />
                    <Line type="monotone" dataKey="total" name="Visitas" stroke="#C9A44C" strokeWidth={2} dot={{ r: 2, fill: '#C9A44C' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[220px] flex items-center justify-center text-[#666] text-sm">Sin visitas registradas aún</div>
              )}
            </div>
          </>
        )}

        {/* Ingresos */}
        {ingresos && (() => {
          const totalSubs = ingresos.por_plan?.reduce((a, p) => a + p.cantidad, 0) || 0;
          const promedio = totalSubs ? ingresos.ingreso_mensual / totalSubs : 0;
          return (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard icon={DollarSign} label="Ingreso mensual estimado" value={fmt(ingresos.ingreso_mensual)}
                  sub={`${ingresos.pct_cambio_mes >= 0 ? '▲' : '▼'} ${Math.abs(ingresos.pct_cambio_mes)}% vs mes anterior`} />
                <StatCard icon={Wallet} label="Ingreso semanal estimado" value={fmt(ingresos.ingreso_semanal)}
                  sub={`${ingresos.pct_cambio_semana >= 0 ? '▲' : '▼'} ${Math.abs(ingresos.pct_cambio_semana)}% vs semana anterior`} />
                <StatCard icon={CreditCard} label="Suscripciones activas" value={totalSubs} sub="▲ pagando ahora" />
                <StatCard icon={TrendingUp} label="Ingreso por suscripción promedio" value={fmt(promedio)} sub="▲ ARS por cliente/mes" />
              </div>

              <div className="grid lg:grid-cols-2 gap-4">
                <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4">
                  <div className="text-xs font-semibold text-[#C9A44C] uppercase tracking-wide mb-4">Ingresos mensuales (ARS)</div>
                  {ingresos.historico_mensual?.length ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={ingresos.historico_mensual}>
                        <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                        <XAxis dataKey="mes" stroke="#666" fontSize={11} />
                        <YAxis stroke="#666" fontSize={11} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                        <Tooltip
                          contentStyle={{ background: '#0E0E0E', border: '1px solid rgba(201,164,76,0.2)', borderRadius: 8, fontSize: 12 }}
                          labelStyle={{ color: '#F8F6F0' }}
                          formatter={(v) => [fmt(v), 'Ingreso ARS']}
                        />
                        <Line type="monotone" dataKey="ingreso" name="Ingreso ARS" stroke="#C9A44C" strokeWidth={2} dot={{ r: 3, fill: '#C9A44C' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[260px] flex items-center justify-center text-[#666] text-sm">Sin datos suficientes</div>
                  )}
                </div>

                <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4">
                  <div className="text-xs font-semibold text-[#C9A44C] uppercase tracking-wide mb-4">Ingresos por plan</div>
                  {ingresos.por_plan?.length ? (
                    <div className="space-y-2">
                      {ingresos.por_plan.map((p) => (
                        <div key={p.plan} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-white/[0.03] text-xs">
                          <div>
                            <div className="text-[#F8F6F0] font-medium">{PLAN_LABELS[p.plan] || p.plan}</div>
                            <div className="text-[#666] mt-0.5">{p.cantidad} suscripciones · {fmt(p.precio_unitario)}/mes c/u</div>
                          </div>
                          <div className="text-[#C9A44C] font-semibold">{fmt(p.ingreso)}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-[260px] flex items-center justify-center text-[#666] text-sm">Sin suscripciones activas aún</div>
                  )}
                </div>
              </div>
            </>
          );
        })()}

        {/* Organizaciones */}
        <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <h2 className="text-sm font-medium flex items-center gap-2"><Building2 className="w-4 h-4 text-[#C9A44C]" /> Organizaciones</h2>
            <div className="flex items-center gap-2">
              <button onClick={purgarHuerfanos} disabled={purgando}
                title="Borra datos que quedaron de orgs eliminadas con la versión vieja"
                className="flex items-center gap-1.5 text-xs font-medium text-[#888] hover:text-red-400 border border-white/10 hover:border-red-400/40 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50">
                <Trash2 className="w-3.5 h-3.5" /> {purgando ? 'Purgando…' : 'Purgar huérfanos'}
              </button>
              <button onClick={() => setShowCrear(true)}
                className="flex items-center gap-1.5 text-xs font-semibold text-[#0E0E0E] rounded-lg px-3 py-1.5"
                style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
                <Plus className="w-3.5 h-3.5" /> Nueva org de prueba
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[#666] uppercase tracking-wide text-[10px] border-b border-white/5">
                  <th className="px-4 py-2.5">Organización</th>
                  <th className="px-4 py-2.5">Plan</th>
                  <th className="px-4 py-2.5">Estado</th>
                  <th className="px-4 py-2.5">Admin</th>
                  <th className="px-4 py-2.5 text-center">Usuarios</th>
                  <th className="px-4 py-2.5 text-center">Expedientes</th>
                  <th className="px-4 py-2.5">Trial hasta</th>
                  <th className="px-4 py-2.5">Creada</th>
                  <th className="px-4 py-2.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading && !orgs && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-[#666]"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                )}
                {orgs?.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-[#666]">No hay organizaciones</td></tr>
                )}
                {orgs?.map((o) => (
                  <tr key={o.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                    <td className="px-4 py-2.5 font-medium">
                      <div>{o.nombre}</div>
                      <button type="button" onClick={() => navigator.clipboard?.writeText(o.id)}
                        title={`org_id: ${o.id} (clic para copiar)`}
                        className="mt-0.5 font-mono text-[10px] text-[#666] hover:text-[#C9A44C] transition-colors">
                        {o.id?.slice(0, 8)}… <span className="opacity-60">copiar</span>
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-[#A0A0A0]">{PLAN_LABELS[o.plan] || o.plan}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wide ${ESTADO_COLOR[o.estado] || ESTADO_COLOR.cancelado}`}>
                        {o.estado}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-[#A0A0A0]">{o.admin_email || '—'}</td>
                    <td className="px-4 py-2.5 text-center">{o.total_usuarios}</td>
                    <td className="px-4 py-2.5 text-center">{o.total_expedientes}</td>
                    <td className="px-4 py-2.5 text-[#A0A0A0]">{o.trial_fin ? fmtF(o.trial_fin) : '—'}</td>
                    <td className="px-4 py-2.5 text-[#A0A0A0]">{fmtF(o.fecha_creacion)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1.5">
                        {busyId === o.id ? (
                          <Loader2 className="w-4 h-4 animate-spin text-[#C9A44C]" />
                        ) : (
                          <>
                            {o.estado !== 'activo' && (
                              <button onClick={() => accion(o, 'activar')} title="Activar"
                                className="p-1.5 rounded-lg text-emerald-400 hover:bg-emerald-500/10 transition-colors"><Power className="w-3.5 h-3.5" /></button>
                            )}
                            {o.estado !== 'suspendido' && (
                              <button onClick={() => accion(o, 'suspender')} title="Suspender"
                                className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"><PowerOff className="w-3.5 h-3.5" /></button>
                            )}
                            <button onClick={() => accion(o, 'extender')} title="Extender trial"
                              className="p-1.5 rounded-lg text-[#C9A44C] hover:bg-[#C9A44C]/10 transition-colors"><Clock className="w-3.5 h-3.5" /></button>
                            <button onClick={() => accion(o, 'eliminar')} title="Eliminar"
                              className="p-1.5 rounded-lg text-[#888] hover:text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actividad reciente */}
        <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5">
            <h2 className="text-sm font-medium flex items-center gap-2"><Activity className="w-4 h-4 text-[#C9A44C]" /> Actividad reciente</h2>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {actividad?.length === 0 && <div className="px-4 py-8 text-center text-[#666] text-sm">Sin actividad reciente</div>}
            {actividad?.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.03] text-xs">
                <div className="min-w-0">
                  <span className="text-[#F8F6F0]">{a.usuario_nombre?.trim() || a.usuario_email || 'Sistema'}</span>
                  <span className="text-[#666]"> · {a.accion} {a.entidad}</span>
                  {a.org_nombre && <span className="text-[#555]"> · {a.org_nombre}</span>}
                </div>
                <span className="text-[#555] flex-shrink-0 ml-3">{fmtF(a.fecha)}</span>
              </div>
            ))}
          </div>
        </div>
      </main>

      {showCrear && (
        <ModalCrearOrg api={api} onClose={() => setShowCrear(false)} onCreated={() => { setShowCrear(false); cargar(); }} />
      )}
    </div>
  );
}

/* ─── Página ───────────────────────────────────────────────── */
export default function SuperAdmin() {
  const [secret, setSecret] = useState(() => {
    try { return sessionStorage.getItem(SECRET_KEY) || ''; } catch { return ''; }
  });

  const logout = () => {
    try { sessionStorage.removeItem(SECRET_KEY); } catch {}
    setSecret('');
  };

  if (!secret) return <Gate onAuth={setSecret} />;
  return <Panel secret={secret} onLogout={logout} />;
}
